import { LinkIcon, Maximize2Icon, VariableIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { v4 } from "uuid";

import { Variable } from "@/components/InputVariableConfigurator/types";
import { usePlayground } from "@/components/Playground/playground-store";
import { usePlaygroundChatSettings } from "@/components/Playground/Drawer/Chat/hooks/usePlaygroundChatSettings";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

interface ScopedInputVariablePopoverProps {
  variableKey: string;
  anchorRect: DOMRect;
  onClose: () => void;
}

const getVariableValueText = (variable?: Variable): string => {
  if (!variable) {
    return "";
  }

  if (typeof variable.value === "string") {
    return variable.value;
  }

  return JSON.stringify(variable.value, null, 2);
};

const getPopoverPosition = (anchorRect: DOMRect) => {
  const width = 360;
  const margin = 12;
  const left = Math.min(Math.max(anchorRect.left, margin), window.innerWidth - width - margin);
  const top = Math.min(anchorRect.bottom + 8, window.innerHeight - margin);

  return { left, top, width };
};

const ScopedInputVariablePopover = observer(
  ({ variableKey, anchorRect, onClose }: ScopedInputVariablePopoverProps) => {
    const playground = usePlayground();
    const popoverRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const closeTimeoutRef = useRef<number | null>(null);
    const [isClosing, setIsClosing] = useState(false);
    const activeSet = playground.activeInputVariableSet;
    const variable = activeSet?.variables.find(v => v.key === variableKey);
    const [draftValue, setDraftValue] = useState(() => getVariableValueText(variable));
    const { isChatMode, isChatSettingsVariableKey, getChatSettingsLabelByVariableKey } =
      usePlaygroundChatSettings(variableKey);
    const isChatSettingsVariable = isChatMode && isChatSettingsVariableKey;
    const chatSettingsLabel = getChatSettingsLabelByVariableKey();

    const variableValue = variable?.value;

    useEffect(() => {
      setDraftValue(getVariableValueText(variable));
    }, [variable, variableValue]);

    useEffect(() => {
      if (!activeSet || isChatSettingsVariable) {
        return;
      }

      textareaRef.current?.focus();
      textareaRef.current?.select();
    }, [activeSet, isChatSettingsVariable]);

    const closePopover = useCallback(
      ({ immediate = false }: { immediate?: boolean } = {}) => {
        if (closeTimeoutRef.current !== null) {
          window.clearTimeout(closeTimeoutRef.current);
          closeTimeoutRef.current = null;
        }

        if (immediate) {
          onClose();
          return;
        }

        setIsClosing(true);
        closeTimeoutRef.current = window.setTimeout(() => {
          closeTimeoutRef.current = null;
          onClose();
        }, 150);
      },
      [onClose],
    );

    useEffect(() => {
      const handlePointerDown = (event: PointerEvent) => {
        const target = event.target instanceof Node ? event.target : null;
        if (target && popoverRef.current?.contains(target)) {
          return;
        }

        // If clicking another input variable, let the click handler manage opening/switching
        if (target instanceof Element && target.closest('[data-input-variable="true"]')) {
          return;
        }

        closePopover();
      };

      const handleKeyDown = (event: globalThis.KeyboardEvent) => {
        if (event.key === "Escape") {
          event.preventDefault();
          closePopover();
        }
      };

      document.addEventListener("pointerdown", handlePointerDown, true);
      document.addEventListener("keydown", handleKeyDown, true);

      return () => {
        document.removeEventListener("pointerdown", handlePointerDown, true);
        document.removeEventListener("keydown", handleKeyDown, true);
        if (closeTimeoutRef.current !== null) {
          window.clearTimeout(closeTimeoutRef.current);
        }
      };
    }, [closePopover]);

    const position = useMemo(() => getPopoverPosition(anchorRect), [anchorRect]);

    const handleCreateSet = () => {
      if (!playground.canAddInputVariableSet) {
        return;
      }
      playground.addEmptyInputVariableSet();
    };

    const handleSave = () => {
      if (!activeSet || isChatSettingsVariable) {
        return;
      }

      playground.updateInputVariable(activeSet.id)(variable?.id ?? v4(), variableKey, draftValue);
      closePopover();
    };

    const handleKeyDown = (event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        event.nativeEvent.stopImmediatePropagation();
        handleSave();
      }
    };

    let bodyContent: ReactNode;

    if (!activeSet) {
      bodyContent = (
        <div className="space-y-3">
          <p className="text-sm leading-5 text-gray-600">
            Values are saved in a set. Create one set to fill this variable, then reuse or switch
            sets for different test cases.
          </p>
          <Button
            type="button"
            size="sm"
            onClick={handleCreateSet}
            disabled={!playground.canAddInputVariableSet}
            className="w-full"
          >
            Create a set
          </Button>
          {!playground.canAddInputVariableSet && (
            <p className="text-xs text-gray-500">You can keep up to 5 input sets.</p>
          )}
        </div>
      );
    } else if (isChatSettingsVariable) {
      bodyContent = (
        <div className="rounded-md border border-blue-100 bg-blue-50 p-3 text-sm text-blue-700">
          <div className="flex items-center gap-2 font-medium">
            <LinkIcon className="h-4 w-4" />
            Linked to chat settings
          </div>
          <p className="mt-1 leading-5 text-blue-600">
            This value is controlled by {chatSettingsLabel}. Update it from chat settings instead of
            editing it here.
          </p>
        </div>
      );
    } else {
      bodyContent = (
        <div className="space-y-3">
          <p className="text-sm leading-5 text-gray-500">
            Set the value used everywhere this variable appears in the prompt.
          </p>
          <Textarea
            ref={textareaRef}
            value={draftValue}
            onChange={event => setDraftValue(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Value for ${variableKey}`}
            className="min-h-[120px] resize-y font-mono text-sm"
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => closePopover()}>
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={handleSave}>
              Save
            </Button>
          </div>
          <div className="text-[11px] text-gray-400">Cmd/Ctrl+Enter to save · Esc to close</div>
        </div>
      );
    }

    return createPortal(
      <div
        ref={popoverRef}
        data-state={isClosing ? "closed" : "open"}
        data-side="bottom"
        className={cn(
          "fixed z-40 rounded-lg border border-gray-200 bg-white p-3 shadow-lg [animation-fill-mode:forwards]",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2",
        )}
        style={{ left: position.left, top: position.top, width: position.width }}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Input variable
            </div>
            <div className="mt-1 truncate font-mono text-sm font-semibold text-gray-900">
              {variableKey}
            </div>
            {activeSet && (
              <div
                className="mt-1.5 flex min-w-0 items-center gap-1.5 text-xs text-gray-500"
                title={`Input scope: ${activeSet.name || "Untitled set"}`}
              >
                <VariableIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                <span className="shrink-0">Input scope</span>
                <span className="truncate font-medium text-gray-700">
                  {activeSet.name || "Untitled set"}
                </span>
              </div>
            )}
          </div>
          {activeSet && !isChatSettingsVariable && (
            <Button
              type="button"
              variant="ghost"
              size="tinyIcon"
              onClick={() => {
                closePopover({ immediate: true });
                playground.startEditingVariableSet(activeSet.id);
              }}
              aria-label="Open full variable editor"
              title="Open full variable editor"
            >
              <Maximize2Icon className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        {bodyContent}
      </div>,
      document.body,
    );
  },
);

export default ScopedInputVariablePopover;
